import { handleActions } from "redux-actions";
import type { IProductColorInsight } from "types/insights/product-color-insight";
import type { IProductSizeInsights } from "types/insights/product-size-insight";
import type { IBadges } from "types/insights/product-badges";
import { INSIGHTS } from "constants/action-types";
import type { ProductStyleInsights } from "models/insights/product-style-insights";
import type { CategoryInsights } from "models/insights/category-insights";
import type { IStore } from "reducers";

type ILoadingState = {
    categories: boolean;
    colors: boolean;
    badges: boolean;
    productStyles: boolean;
    productSizes: boolean;
};

export const isCurrencyCodeUSDSelector = (state: IStore) => {
    return state?.scope?.currencyCode === "USD";
};

export const isCompanyCurrencyCodeUSDSelector = (state: IStore) => {
    return state?.scope?.company?.__currency_codes.includes("USD");
};

export const isInsightsButtonEnabledSelector = (state: IStore) => {
    return (
        isCurrencyCodeUSDSelector(state) &&
        !state.insights.isLoading.productStyles &&
        !state.insights.isLoading.colors &&
        !state.insights.isLoading.categories &&
        (state.insights.productStyles?.immediate?.length > 0 ||
            state.insights.productStyles?.prebook?.length > 0 ||
            state.insights.productColors !== null ||
            state.insights.productSizes?.length > 0 ||
            state.insights.categories?.length > 0)
    );
};

// The pathname is passed in from the component's props because it cannot be accessed directly from the store.
// see https://github.com/reactjs/react-router-redux?tab=readme-ov-file#how-do-i-access-router-state-in-a-container-component
export const isInsightsPaneStateSavedAsOpenSelector = (state: IStore, pageOrPathname: string) => {
    if (pageOrPathname.includes("gallery")) {
        return state.insights.galleryInsightsSidePanelOpen;
    } else if (pageOrPathname.includes("cart")) {
        return state.insights.cartInsightsSidePanelOpen;
    } else {
        return false;
    }
};

export const isInsightsPanelOpenedSelector = (state: IStore, pageOrPathname: "gallery" | "cart" | string) => {
    return isInsightsButtonEnabledSelector(state) && isInsightsPaneStateSavedAsOpenSelector(state, pageOrPathname);
};

type ProductStyles = {
    immediate?: ProductStyleInsights[];
    prebook?: ProductStyleInsights[];
};
export interface IInsightsState {
    productStyles: ProductStyles;
    categories: CategoryInsights[];
    badges: IBadges;
    cartInsightsSidePanelOpen: boolean;
    galleryInsightsSidePanelOpen: boolean;
    productColors: IProductColorInsight[] | null;
    productSizes: IProductSizeInsights;
    isLoading: ILoadingState;
    cartUpdating: boolean;
}

export const insights = handleActions<IInsightsState>(
    {
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productStyles: true,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    categories: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    colors: true,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    badges: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.START]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productSizes: true,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.COMPLETED]: (
            state: IInsightsState,
            { productStyles }: { productStyles: ProductStyles }
        ) => {
            return {
                ...state,
                productStyles,
                isLoading: {
                    ...state.isLoading,
                    productStyles: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_STYLE_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productStyles: false,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.COMPLETED]: (
            state: IInsightsState,
            { categories }: { categories: CategoryInsights[] }
        ) => {
            return {
                ...state,
                categories,
                isLoading: {
                    ...state.isLoading,
                    categories: false,
                },
            };
        },
        [INSIGHTS.FETCH_CATEGORY_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    categories: false,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.COMPLETED]: (state: IInsightsState, { badges }: any) => {
            return {
                ...state,
                badges: badges,
                isLoading: {
                    ...state.isLoading,
                    badges: false,
                },
            };
        },
        [INSIGHTS.FETCH_BADGES.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    badges: false,
                },
            };
        },
        [INSIGHTS.CART_SIDE_PANEL_TOGGLE]: (state) => {
            return {
                ...state,
                cartInsightsSidePanelOpen: !state.cartInsightsSidePanelOpen,
            };
        },
        [INSIGHTS.GALLERY_SIDE_PANEL_TOGGLE]: (state) => {
            return {
                ...state,
                galleryInsightsSidePanelOpen: !state.galleryInsightsSidePanelOpen,
            };
        },
        [INSIGHTS.GALLERY_SIDE_PANEL_CLOSE]: (state) => {
            return {
                ...state,
                galleryInsightsSidePanelOpen: false,
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.COMPLETED]: (state: IInsightsState, { productColors }: any) => {
            return {
                ...state,
                productColors,
                isLoading: {
                    ...state.isLoading,
                    colors: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_COLOR_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    colors: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.COMPLETED]: (state: IInsightsState, { productSizes }: any) => {
            return {
                ...state,
                productSizes,
                isLoading: {
                    ...state.isLoading,
                    productSizes: false,
                },
            };
        },
        [INSIGHTS.FETCH_PRODUCT_SIZE_INSIGHTS.ERROR]: (state: IInsightsState) => {
            return {
                ...state,
                isLoading: {
                    ...state.isLoading,
                    productSizes: false,
                },
            };
        },
        [INSIGHTS.CLEAR]: (state: IInsightsState) => {
            return {
                ...state,
                productStyles: {},
                categories: [],
                badges: undefined,
                productColors: null,
                productSizes: [],
            };
        },
        [INSIGHTS.SIZE_INSIGHTS.REMOVE_PRODUCT]: (state: IInsightsState, { productToRemoveId }: any) => {
            return {
                ...state,
                productSizes: state.productSizes.filter((product) => product.productId !== productToRemoveId),
            };
        },
    },
    {
        productStyles: {},
        categories: [],
        badges: undefined,
        cartInsightsSidePanelOpen: false,
        galleryInsightsSidePanelOpen: true,
        productColors: null,
        productSizes: [],
        isLoading: {
            categories: false,
            colors: false,
            badges: false,
            productStyles: false,
            productSizes: false,
        },
        cartUpdating: false,
    }
);
